import { api } from "./api";

/**
 * Upload a single image file to /api/upload with automatic retries on
 * transient network failures (timeouts, 5xx, ECONNABORTED).
 *
 * - Attempts: 1 initial + 3 retries = 4 total tries
 * - Backoff: 1s, 3s, 8s (capped)
 * - Aborts retries if the file isn't actually transferable
 *
 * @param {File|Blob} file
 * @param {{ filename?: string, onAttempt?: (attempt:number)=>void }} opts
 * @returns {Promise<{path:string}>}
 */
export async function uploadWithRetry(file, opts = {}) {
  const { onAttempt } = opts;
  const filename = opts.filename
    || (file instanceof File ? file.name : "foto.jpg")
    || "foto.jpg";

  const delays = [0, 1000, 3000, 8000];
  let lastErr = null;
  for (let i = 0; i < delays.length; i++) {
    if (delays[i] > 0) {
      await new Promise((r) => setTimeout(r, delays[i]));
    }
    try {
      onAttempt?.(i + 1);
      const fd = new FormData();
      // Always wrap as File so backend gets a proper filename
      const f = file instanceof File ? file : new File([file], filename, { type: file.type || "image/jpeg" });
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      return data;
    } catch (err) {
      lastErr = err;
      // Non-retriable: explicit 4xx (e.g. 400/413/415) → give up immediately
      const status = err?.response?.status;
      if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        throw err;
      }
      // Otherwise keep retrying
    }
  }
  throw lastErr || new Error("Upload fallito");
}
